import { useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "motion/react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { auctionItems } from "@/mocks/auctionItems";
import ItemPicker from "./components/ItemPicker";
import CompareGrid from "./components/CompareGrid";

const MAX_COMPARE = 4;

export default function ComparePage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialIds = useMemo(() => {
    const raw = searchParams.get("items") ?? "";
    return raw
      .split(",")
      .filter(Boolean)
      .filter((id) => auctionItems.some((a) => a.id === id))
      .slice(0, MAX_COMPARE);
  }, []);

  const [selectedIds, setSelectedIds] = useState<string[]>(initialIds);
  const [showPicker, setShowPicker] = useState(initialIds.length === 0);

  const addItem = useCallback(
    (id: string) => {
      if (selectedIds.length >= MAX_COMPARE || selectedIds.includes(id)) return;
      const next = [...selectedIds, id];
      setSelectedIds(next);
      setSearchParams({ items: next.join(",") });
    },
    [selectedIds, setSearchParams]
  );

  const removeItem = useCallback(
    (id: string) => {
      const next = selectedIds.filter((sid) => sid !== id);
      setSelectedIds(next);
      if (next.length) setSearchParams({ items: next.join(",") });
      else setSearchParams({});
    },
    [selectedIds, setSearchParams]
  );

  const clearAll = useCallback(() => {
    setSelectedIds([]);
    setSearchParams({});
    setShowPicker(true);
  }, [setSearchParams]);

  const selectedItems = selectedIds
    .map((id) => auctionItems.find((a) => a.id === id))
    .filter(Boolean) as typeof auctionItems;

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[1400px] mx-auto">

          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="mb-6"
          >
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-bar-chart-horizontal-line text-[#C4956A]"></i>
              <span className="text-xs font-medium text-[#C4956A]">도구</span>
            </div>
            <div className="flex items-start justify-between gap-4 flex-col sm:flex-row">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-[#1C1917] mb-1">
                  물건 비교 계산기
                </h1>
                <p className="text-sm text-[#8C8780]">
                  최대 {MAX_COMPARE}개 물건을 나란히 비교 — 일반방식·시세역산 결과를 한눈에 확인
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {selectedIds.length > 0 && (
                  <button
                    onClick={clearAll}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#8C8780] border border-[#E8E5E0] rounded-full hover:border-rose-300 hover:text-rose-500 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line"></i>
                    전체 초기화
                  </button>
                )}
                <Link
                  to="/bid-calculator"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#C4956A] border border-[#C4956A]/30 rounded-full hover:bg-[#C4956A]/5 transition-all whitespace-nowrap"
                >
                  <i className="ri-calculator-line"></i>
                  상세 계산기
                </Link>
              </div>
            </div>
          </motion.div>

          {/* 사용 안내 (아이템 없을 때만 표시) */}
          {selectedIds.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.4 }}
              className="mb-6 bg-gradient-to-r from-[#1C1917] to-[#3A2E28] rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <div className="w-10 h-10 rounded-xl bg-[#C4956A]/20 flex items-center justify-center flex-shrink-0">
                <i className="ri-lightbulb-line text-[#C4956A] text-lg"></i>
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-white mb-1">이렇게 사용하세요</p>
                <div className="flex flex-wrap gap-3 text-xs text-[#B8B3AE]">
                  <span className="flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded-full bg-[#C4956A]/30 flex items-center justify-center text-[#C4956A] font-bold text-[10px]">1</span>
                    아래에서 물건 선택 (최대 4개)
                  </span>
                  <i className="ri-arrow-right-line text-[#5C5650]"></i>
                  <span className="flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded-full bg-[#C4956A]/30 flex items-center justify-center text-[#C4956A] font-bold text-[10px]">2</span>
                    두 계산방식 결과 비교
                  </span>
                  <i className="ri-arrow-right-line text-[#5C5650]"></i>
                  <span className="flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded-full bg-[#C4956A]/30 flex items-center justify-center text-[#C4956A] font-bold text-[10px]">3</span>
                    최고 ROI·최저 비용 하이라이트 확인
                  </span>
                </div>
              </div>
            </motion.div>
          )}

          {/* 물건 선택 패널 */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.4 }}
          >
            <ItemPicker
              selectedIds={selectedIds}
              onAdd={addItem}
              onRemove={removeItem}
              maxItems={MAX_COMPARE}
              show={showPicker}
              onToggle={() => setShowPicker((v) => !v)}
            />
          </motion.div>

          {/* 선택된 물건 있으면 비교 그리드 */}
          {selectedItems.length > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              {/* 선택 물건 태그 */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-xs text-[#8C8780]">비교 중:</span>
                {selectedItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-[#E8E5E0] rounded-full"
                  >
                    <span className="text-xs font-medium text-[#1C1917]">{item.location}</span>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="w-4 h-4 rounded-full bg-[#F0EDE8] flex items-center justify-center hover:bg-rose-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-close-line text-[9px] text-[#8C8780]"></i>
                    </button>
                  </div>
                ))}
                {selectedItems.length < MAX_COMPARE && (
                  <button
                    onClick={() => setShowPicker(true)}
                    className="flex items-center gap-1 px-2.5 py-1 border border-dashed border-[#C4956A]/50 text-[#C4956A] text-xs rounded-full hover:bg-[#C4956A]/5 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line text-xs"></i>
                    물건 추가
                  </button>
                )}
              </div>

              {/* 비교 그리드 */}
              <CompareGrid items={selectedItems} onRemove={removeItem} />
            </motion.div>
          ) : (
            /* 빈 상태 */
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="text-center py-20 bg-white rounded-xl border border-dashed border-[#E8E5E0]"
            >
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#C4956A]/10 flex items-center justify-center">
                <i className="ri-bar-chart-horizontal-line text-2xl text-[#C4956A]"></i>
              </div>
              <p className="text-sm font-semibold text-[#1C1917] mb-1">물건을 선택하면 비교가 시작됩니다</p>
              <p className="text-xs text-[#8C8780] mb-5">위에서 비교하고 싶은 물건을 1개 이상 선택하세요</p>
              <button
                onClick={() => setShowPicker(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1C1917] text-white text-sm font-medium rounded-full hover:bg-[#C4956A] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-circle-line"></i>
                물건 선택 시작
              </button>
            </motion.div>
          )}

          {/* 하단 링크 */}
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/auctions"
              className="flex items-center gap-2 px-4 py-2 text-sm text-[#5C5650] border border-[#E8E5E0] rounded-full hover:border-[#C4956A] hover:text-[#C4956A] transition-all"
            >
              <i className="ri-auction-line"></i>
              경매 물건 목록
            </Link>
            <Link
              to="/bid-calculator"
              className="flex items-center gap-2 px-4 py-2 text-sm text-[#5C5650] border border-[#E8E5E0] rounded-full hover:border-[#C4956A] hover:text-[#C4956A] transition-all"
            >
              <i className="ri-calculator-line"></i>
              상세 입찰가 계산기
            </Link>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}